import { useChartColors } from '../lib/accessibility'
import { apiError } from '../lib/errors'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Sprout, X, TrendingUp, CheckCircle2, XCircle, Clock } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { propagationApi, varietiesApi, type PropagationBatch, type SuccessRateSummary } from '../lib/api'

const C = {
  bg:      'var(--color-bg)',
  card:    'var(--color-bg-card)',
  card2:   '#1A2314',
  border:  'var(--color-border)',
  amber:   'var(--color-primary)',
  green:   '#4CAF50',
  text:    'var(--color-text)',
  muted:   'var(--color-text-muted)',
  faint:   'var(--color-text-faint)',
  danger:  '#F87171',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.muted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

const BATCH_TYPES = ['graft', 'cutting', 'seed', 'air_layer', 'division']
const STATUSES    = ['active', 'completed', 'failed', 'cancelled']

function statusColor(s: string) {
  if (s === 'active')    return C.amber
  if (s === 'completed') return C.green
  if (s === 'failed' || s === 'cancelled') return C.danger
  return C.faint
}
function statusIcon(s: string) {
  if (s === 'active')    return <Clock size={13} style={{ color: C.amber }} />
  if (s === 'completed') return <CheckCircle2 size={13} style={{ color: C.green }} />
  if (s === 'failed' || s === 'cancelled') return <XCircle size={13} style={{ color: C.danger }} />
  return null
}

function rateColor(rate: number) {
  if (rate >= 80) return C.green
  if (rate >= 50) return C.amber
  return C.danger
}

function BatchModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: '', type: 'graft', variety_id: '',
    started_at: new Date().toISOString().slice(0, 10),
    total_seedlings: '0',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const { data: varietiesData } = useQuery({
    queryKey: ['varieties', 'all'],
    queryFn: () => varietiesApi.list().then(r => r.data),
  })

  const save = useMutation({
    mutationFn: (data: Partial<PropagationBatch>) => propagationApi.createBatch(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['propagation-batches'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Batch name required'); return }
    save.mutate({
      name: form.name, type: form.type, variety_id: form.variety_id || undefined,
      started_at: new Date(form.started_at).toISOString(),
      total_seedlings: parseInt(form.total_seedlings) || 0,
      status: 'active',
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 420, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>New propagation batch</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={lbl}>Batch name *</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Spring Keitt graft batch" required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Method</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.type} onChange={e => set('type', e.target.value)}>
                {BATCH_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Start date</label><input style={inp} type="date" value={form.started_at} onChange={e => set('started_at', e.target.value)} /></div>
          </div>
          <div>
            <label style={lbl}>Variety</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.variety_id} onChange={e => set('variety_id', e.target.value)}>
              <option value="">— Select variety —</option>
              {(varietiesData?.varieties ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Starting count</label><input style={inp} type="number" min={0} value={form.total_seedlings} onChange={e => set('total_seedlings', e.target.value)} /></div>
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Creating…' : 'Create batch'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SuccessRateChart({ data }: { data: SuccessRateSummary[] }) {
  const chartC = useChartColors()
  if (data.length === 0) return null
  const chartData = data
    .sort((a, b) => b.success_rate - a.success_rate)
    .slice(0, 12)
    .map(s => ({ name: s.variety_name, rate: Math.round(s.success_rate), attempts: s.total_attempts, method: s.batch_type }))

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <TrendingUp size={15} style={{ color: C.amber }} />
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Success Rates by Variety</h2>
        <span style={{ fontSize: 11, color: C.faint, marginLeft: 4 }}>{data.length} varieties tracked</span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 48, left: 8, bottom: 0 }}>
          <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" width={110} tick={{ fill: chartC.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.text }}
            formatter={(val: number, _: string, entry: { payload?: { attempts?: number; method?: string } }) => [`${val}% (${entry.payload?.attempts ?? 0} attempts)`, entry.payload?.method ?? '']}
            labelStyle={{ color: C.text, fontWeight: 600 }}
            cursor={{ fill: 'rgba(255,255,255,0.03)' }}
          />
          <Bar dataKey="rate" radius={[0, 4, 4, 0]} maxBarSize={18}>
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={rateColor(entry.rate)} opacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Table below chart */}
      <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map(s => (
          <div key={`${s.variety_name}-${s.batch_type}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.02)' }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: C.text }}>{s.variety_name}</p>
              <p style={{ margin: '1px 0 0', fontSize: 10, color: C.faint }}>{s.batch_type} · {s.total_attempts} attempts{s.avg_days_to_take ? ` · ${s.avg_days_to_take}d avg take` : ''}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: rateColor(s.success_rate) }}>{s.success_rate.toFixed(0)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SuccessBar({ rate }: { rate: number }) {
  return (
    <div style={{ width: '100%', height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(rate, 100)}%`, height: '100%', background: rateColor(rate), borderRadius: 2, transition: 'width 0.4s' }} />
    </div>
  )
}

export function PropagationPage() {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ['propagation-batches'],
    queryFn: () => propagationApi.listBatches().then(r => r.data.batches),
  })
  const { data: successRates = [] } = useQuery({
    queryKey: ['propagation-success-rates'],
    queryFn: () => propagationApi.successRates().then(r => r.data),
    staleTime: 5 * 60_000,
  })

  const updateBatch = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PropagationBatch> }) =>
      propagationApi.updateBatch(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['propagation-batches'] }),
  })

  const filtered      = filterStatus === 'all' ? batches : batches.filter(b => b.status === filterStatus)
  const activeBatches = batches.filter(b => b.status === 'active')
  const totalSeedlings = activeBatches.reduce((s, b) => s + b.alive_seedlings, 0)
  const avgRate = batches.length > 0
    ? batches.reduce((s, b) => s + b.success_rate, 0) / batches.length
    : 0

  function daysElapsed(started: string) {
    return Math.floor((Date.now() - new Date(started).getTime()) / 86_400_000)
  }

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, fontFamily: '"DM Sans", system-ui, sans-serif', color: C.text }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>Propagation</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>Grafting, cuttings, and seedling batches — track success rates</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.amber, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> New batch
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Active batches',   value: activeBatches.length,     color: C.amber },
          { label: 'Live seedlings',   value: totalSeedlings,            color: C.green },
          { label: 'Total batches',    value: batches.length,            color: C.text },
          { label: 'Avg success rate', value: `${avgRate.toFixed(0)}%`,  color: rateColor(avgRate) },
        ].map(s => (
          <div key={s.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
            <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {['all', ...STATUSES].map(s => {
          const count = s === 'all' ? batches.length : batches.filter(b => b.status === s).length
          return (
            <button key={s} onClick={() => setFilterStatus(s)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: filterStatus === s ? 600 : 400, cursor: 'pointer', background: filterStatus === s ? C.amber : C.card, color: filterStatus === s ? C.bg : C.muted, border: `1px solid ${filterStatus === s ? C.amber : C.border}` }}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              {count > 0 && <span style={{ marginLeft: 5, opacity: 0.7 }}>{count}</span>}
            </button>
          )
        })}
      </div>

      {/* Batch list */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
          <Sprout size={32} style={{ color: C.faint, marginBottom: 12, opacity: 0.5 }} />
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No batches yet.</p>
          <p style={{ fontSize: 12, color: C.faint, margin: '6px 0 16px' }}>Start a batch to track graft takes, seedling survival, and success rates.</p>
          <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>New batch</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(b => {
            const days = daysElapsed(b.started_at)
            return (
              <div key={b.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      {statusIcon(b.status)}
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>{b.name}</p>
                      <span style={{ padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: statusColor(b.status) + '20', color: statusColor(b.status) }}>{b.status}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 12, color: C.faint }}>
                      {b.type.replace('_', ' ')} · Started {new Date(b.started_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                      {b.status === 'active' && <span style={{ color: C.amber }}> · {days}d elapsed</span>}
                    </p>
                  </div>
                  {b.status === 'active' && (
                    <button
                      onClick={() => updateBatch.mutate({ id: b.id, data: { status: 'completed' } })}
                      style={{ padding: '5px 12px', borderRadius: 7, background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.3)', color: C.green, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
                      Mark complete
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 10 }}>
                  {[
                    { label: 'Started',   value: b.total_seedlings },
                    { label: 'Alive',     value: b.alive_seedlings },
                    { label: 'Lost',      value: b.total_seedlings - b.alive_seedlings },
                    { label: 'Take rate', value: `${b.success_rate.toFixed(0)}%`, color: rateColor(b.success_rate) },
                  ].map(s => (
                    <div key={s.label}>
                      <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</p>
                      <p style={{ margin: '3px 0 0', fontSize: 18, fontWeight: 700, color: (s as { color?: string }).color ?? C.text }}>{s.value}</p>
                    </div>
                  ))}
                </div>
                <SuccessBar rate={b.success_rate} />
              </div>
            )
          })}
        </div>
      )}

      {/* Success rate chart */}
      <SuccessRateChart data={successRates} />

      {showModal && <BatchModal onClose={() => setShowModal(false)} />}
    </main>
  )
}
